import React, { FC, useCallback, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Table, TableRow, TextArea } from '@code/innovance-react-ui-kit';

import {
  Title,
  TableWrapper,
  Separator,
  TextAreaContainer,
  FormRow,
} from 'src/front/components/FormStyles';

import { ProblemTrackingSystemApi } from 'src/front/api/apiInstance';

import { TABLE_WIDTHS } from 'src/front/constant';

interface Props {
  problemId: string;
}

const RecordHistoryTableHeaders = [
  'problemTrackingSystem_dateEntry_label',
  'problemTrackingSystem_inputTime_label',
  'general_components_userCode_label',
  'problemTrackingSystem_department_label',
  'problemTrackingSystem_typeOfAction_label',
  'problemTrackingSystem_newValue_label',
];

const RecordHistoryTable: FC<Props> = ({ problemId }) => {
  const { t } = useTranslation();

  const [page, setPage] = useState<number>(1);
  const [history, setHistory] = useState<TableRow[]>([]);
  const [selectedHistory, setSelectedHistory] = useState<TableRow>();
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [isLoadingTable, setIsLoadingTable] = useState<boolean>(false);

  const loadHistory = useCallback(
    async (pageNumber = 1, forceRefresh = false) => {
      setIsLoadingTable(true);

      const { data } = await ProblemTrackingSystemApi.getPtsRecordHistory({
        problemId,
        rowCountPerPage: 10,
        pageNumber: pageNumber || page,
        refresh: forceRefresh,
      });

      if (data.result === 'OK') {
        setPage(pageNumber || 1);
        setHistory(
          data.recordList.map((record) => ({
            id: `${record.dateOfEntry}-${Math.random() * 5}`,
            columns: [
              record.dateOfEntry,
              record.inputTime,
              record.userCode,
              record.department,
              record.typeOfAction,
              record.newValue,
            ],
          }))
        );
        setHasNextPage(data.hasNextPage);
      }

      setIsLoadingTable(false);
    },
    [page, problemId]
  );

  useEffect(() => {
    loadHistory(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [problemId]);

  const handleSelectHistory = (historyRecord: TableRow | undefined): void => {
    setSelectedHistory(historyRecord);
  };

  return (
    <>
      <Title>
        <Trans i18nKey="problemTrackingSystem_historyOfRecord_text" />
      </Title>
      <Separator />
      <TableWrapper>
        <Table
          emptyTableText={t('general_components_notFound_errorMessage')}
          hasNextPage={hasNextPage}
          headers={RecordHistoryTableHeaders.map((head) => t(head))}
          items={history}
          loading={isLoadingTable}
          loadPageItems={loadHistory}
          onSelect={handleSelectHistory}
          pageNumber={page}
          selectedItem={selectedHistory}
          tableWidth={TABLE_WIDTHS.DESKTOP_3X}
          widths={['10', '10', '10', '10', '10', '50']}
          clearItemOnPageChange
          withRadio
        />
      </TableWrapper>
      <Separator />
      <FormRow>
        <TextAreaContainer>
          <TextArea
            label={t('problemTrackingSystem_valueDetail_label')}
            maxLength={1250}
            value={selectedHistory?.columns[5]}
            disabled
          />
        </TextAreaContainer>
      </FormRow>
    </>
  );
};

export default RecordHistoryTable;
